import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchTypeTable,
  selectMaintenances,
} from "../../../store/slices/maintenances";
import {
  selectStudentTicketsUpdate,
  selectSubTopics,
  tryCreateStudentTicket,
  tryGetSubtopics,
} from "../../../store/slices/student-tickets";
import { selectCurrentUser } from "../../../store/slices/currentUser";
import {
  Button,
  Drawer,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Box,
  Stack,
} from "@mui/material";
import {
  Close,
  UploadFileOutlined,
  CloudUploadOutlined,
} from "@mui/icons-material";
import ModalMessages from "../../layout/modal-messages";
import AddStudentTicketSkeleton from "../skeleton/AddStudentTicketSkeleton";
import { useDropzone } from "react-dropzone";
import { useFormik } from "formik";
import convertToSelectList from "../../layout/functions/convert-to-select-list";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

const validate = (values) => {
  const errors = {};

  const { topic, subtopic, subject, message } = values;

  if (!topic) {
    errors.topic = "Requerido";
  }
  if (!subtopic) {
    errors.subtopic = "Requerido";
  }
  if (!subject) {
    errors.subject = "Requerido";
  }
  if (!message) {
    errors.message = "Requerido";
  }

  return errors;
};

const AddStudentTicketDrawer = ({
  openDrawer,
  setOpenDrawer,
  setOpenDialog,
  businessId,
  clientId,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const currentUser = useSelector(selectCurrentUser);
  const typeTables = useSelector(selectMaintenances);
  const updateStudentTickets = useSelector(selectStudentTicketsUpdate);
  const { isLoading, errors } = updateStudentTickets;
  const [topicId, setTopicId] = useState("");
  const subtopics = useSelector(selectSubTopics);

  useEffect(() => {
    dispatch(fetchTypeTable(51)); //TBLTOPICS
  }, []);

  useEffect(() => {
    if (topicId) {
      dispatch(tryGetSubtopics(topicId));
    }
  }, [topicId]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      topic: 0,
      subtopic: 0,
      subject: "",
      message: "",
      attachment: "",
    },
    validate,
    onSubmit: async (values) => {
      const request = {};
      request.ticketDetails = [];

      request.ticketDetails.push({
        ticketDetailId: 0,
        ticketId: 0,
        senderId: currentUser.roleId !== 1 ? clientId : currentUser.clientId,
        message: values.message,
        attachment: values.attachment,
        statusMessageId: 1,
      });

      request.ticketId = 0;
      request.ownerId = 0;
      request.originalSenderId = clientId;
      request.topicId = values.topic;
      request.subTopicId = values.subtopic;
      request.subject = values.subject;
      request.statusTicketId = 1;
      request.closeDate = null;

      const action = await dispatch(
        tryCreateStudentTicket({ businessId, request })
      );

      if (action.payload.data.isValid) {
        setOpenDrawer(false);
        setOpenDialog(true);
      }
    },
  });

  const { getRootProps, getInputProps, acceptedFiles } = useDropzone({
    noKeyboard: true,
    accept: {
      "image/png": [".png"],
      "image/jpeg": [".jpeg", ".jpg"],
      "application/pdf": [".pdf"],
    },
    maxFiles: 1,
    multiple: false,
    maxSize: 10000000,
    onDropAccepted: (acceptedFiles) => {
      const file = acceptedFiles[0];
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        const base64data = reader.result;
        formik.setFieldValue("attachment", base64data);
      };
    },
  });

  const files = acceptedFiles.map((filename) => (
    <ListItem key={filename.path}>
      <ListItemIcon>
        <UploadFileOutlined color="primary" />
      </ListItemIcon>
      <ListItemText primary={filename.path} />
    </ListItem>
  ));

  return (
    <>
      <Drawer
        anchor={isMobile ? "bottom" : "right"}
        open={openDrawer}
        PaperProps={{
          sx: {
            zIndex: 1203,
            width: { xs: "100vw", sm: 500 },
            maxWidth: "100%",
            maxHeight: { xs: "90vh", sm: "100%" },
            borderRadius: { xs: "12px 12px 0 0", sm: 0 },
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
          },
        }}
        onClose={() => setOpenDrawer(false)}
        sx={{
          zIndex: 1203,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            bgcolor: "#194B6F",
            color: "white",
            position: "relative",
            px: { xs: 2, sm: 3 },
            pt: { xs: 2, sm: 4 },
            pb: { xs: 1.5, sm: 3 },
          }}
        >
          <IconButton
            sx={{
              position: "absolute",
              top: { xs: 8, sm: 16 },
              right: { xs: 8, sm: 16 },
              color: "white",
            }}
            onClick={() => setOpenDrawer(false)}
          >
            <Close />
          </IconButton>

          <Typography
            align="left"
            sx={{
              fontWeight: 700,
              fontSize: 18,
              mb: { xs: 0, sm: 1 },
              pr: 4,
            }}
          >
            Agregar nuevo mensaje
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            backgroundColor: "white",
            p: { xs: 2, sm: 3 },
          }}
        >
          <Grid container rowSpacing={3} spacing={{ xs: 2, sm: 3 }}>
            {isLoading ? (
              <AddStudentTicketSkeleton />
            ) : (
              <>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="topic-label">
                      Tópico asociado al mensaje
                    </InputLabel>
                    <Select
                      labelId="topic-label"
                      name="topic"
                      label="Tópico asociado al mensaje"
                      size="small"
                      value={formik.values.topic}
                      onChange={(e) => {
                        const { value } = e.target;
                        setTopicId(value);
                        formik.setFieldValue("topic", value);
                      }}
                      error={Boolean(formik.touched.topic && formik.errors.topic)}
                    >
                      {typeTables.TBLTOPICS &&
                        convertToSelectList(typeTables.TBLTOPICS.data).map(
                          (x) => (
                            <MenuItem key={x.value} value={x.value}>
                              {x.text}
                            </MenuItem>
                          )
                        )}
                    </Select>
                    <FormHelperText
                      htmlFor="topic"
                      error={formik.touched.topic && !!formik.errors.topic}
                    >
                      {formik.errors.topic}
                    </FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="subtopic-label">
                      SubTópico asociado al mensaje
                    </InputLabel>
                    <Select
                      labelId="subtopic-label"
                      name="subtopic"
                      label="SubTópico asociado al mensaje"
                      size="small"
                      value={formik.values.subtopic}
                      onChange={formik.handleChange}
                      error={Boolean(
                        formik.touched.subtopic && formik.errors.subtopic
                      )}
                    >
                      {subtopics &&
                        subtopics.data.map((x) => (
                          <MenuItem key={x.subTopicId} value={x.subTopicId}>
                            {x.description}
                          </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText
                      htmlFor="subtopic"
                      error={formik.touched.subtopic && !!formik.errors.subtopic}
                    >
                      {formik.errors.subtopic}
                    </FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="subject"
                    label="Asunto"
                    variant="outlined"
                    size="small"
                    fullWidth
                    onChange={formik.handleChange}
                    value={formik.values.subject}
                    error={Boolean(
                      formik.touched.subject && formik.errors.subject
                    )}
                    helperText={formik.touched.subject && formik.errors.subject}
                  />
                </Grid>
                <Grid item xs={12} whiteSpace="normal">
                  <TextField
                    name="message"
                    label="Mensaje"
                    multiline
                    rows={5}
                    fullWidth
                    onChange={formik.handleChange}
                    value={formik.values.message}
                    error={Boolean(
                      formik.touched.message && formik.errors.message
                    )}
                    helperText={formik.touched.message && formik.errors.message}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Paper
                    elevation={3}
                    sx={{
                      border: "4px",
                      borderRadius: "12px",
                      borderStyle: "solid",
                      borderColor: "#85BFE6",
                      marginTop: 2,
                    }}
                  >
                    <section className="container">
                      <div {...getRootProps({ className: "dropzone", style: { cursor: "pointer" } })}>
                        <input {...getInputProps()} />
                        <Grid container columns={{ xs: 12, sm: 12 }} spacing={2} alignItems="center" p={2}>
                          <Grid item xs={12} sm={2} display="flex" justifyContent="center">
                            <CloudUploadOutlined color="primary" fontSize="large" />
                          </Grid>
                          <Grid item xs={12} sm={6} textAlign={{ xs: "center", sm: "left" }}>
                            <Typography color="primary" variant="subtitle2" fontWeight={700}>
                              Seleccione un archivo o arrástrelo hasta acá
                            </Typography>
                            <Typography color="primary" variant="caption">
                              JPG, PNG o PDF, tamaño no mayor a 10MB
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={4} display="flex" justifyContent="center">
                            <Button variant="outlined" color="secondary" sx={{ border: 2 }}>
                              Buscar
                            </Button>
                          </Grid>
                        </Grid>
                      </div>
                      <List sx={{ width: "100%" }}>{files}</List>
                    </section>
                  </Paper>
                </Grid>

                <Grid item xs={12} sx={{ textAlign: "center" }}>
                  {isMobile ? (
                    <Stack direction="column" spacing={2} sx={{ mt: 1, mb: 2 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={formik.handleSubmit}
                        disabled={updateStudentTickets.isLoading}
                      >
                        Enviar
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        color="error"
                        onClick={() => setOpenDrawer(false)}
                      >
                        Cancelar
                      </Button>
                    </Stack>
                  ) : (
                    <Box sx={{ mt: 2, mb: 2, display: "inline-block" }}>
                      <Button
                        variant="contained"
                        onClick={formik.handleSubmit}
                        disabled={updateStudentTickets.isLoading}
                        sx={{ width: 100, marginRight: 2 }}
                      >
                        Enviar
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        sx={{ border: 2, width: 100 }}
                        onClick={() => setOpenDrawer(false)}
                      >
                        Cancelar
                      </Button>
                    </Box>
                  )}
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </Drawer>
      {errors && errors.length > 0 && <ModalMessages errors={errors} />}
    </>
  );
};

export default AddStudentTicketDrawer;